import { useCallback, useEffect, useRef } from 'react';
import { useLinkModal } from './useLinkModal';
import { useUserFavoritesContext } from '../contexts/UserFavoritesContext';
import { detectKindFromUrl } from '../utils/urlUtils';

/**
 * Drop-in replacement for useLinkModal that automatically
 * tracks how long a resource modal is open and records
 * the viewing session via recordResourceVisit.
 *
 * Only counts time the page is visible and focused,
 * so switching tabs/windows pauses the timer.
 */
export const useTrackedLinkModal = () => {
  const linkModal = useLinkModal();
  const { recordResourceVisit } = useUserFavoritesContext();

  const openedAtRef = useRef<string | null>(null);
  const currentUrlRef = useRef<string>('');
  const currentTitleRef = useRef<string>('');

  // Visibility-aware duration tracking
  const focusedMsRef = useRef(0);
  const focusStartRef = useRef<number | null>(null);

  const isFocused = useCallback(() => {
    return document.visibilityState === 'visible' && document.hasFocus();
  }, []);

  const pauseTimer = useCallback(() => {
    if (focusStartRef.current !== null) {
      focusedMsRef.current += Date.now() - focusStartRef.current;
      focusStartRef.current = null;
    }
  }, []);

  const resumeTimer = useCallback(() => {
    if (openedAtRef.current && focusStartRef.current === null && isFocused()) {
      focusStartRef.current = Date.now();
    }
  }, [isFocused]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      pauseTimer();
    } else {
      resumeTimer();
    }
  }, [pauseTimer, resumeTimer]);

  const handleWindowBlur = useCallback(() => pauseTimer(), [pauseTimer]);
  const handleWindowFocus = useCallback(() => resumeTimer(), [resumeTimer]);

  // Register/unregister visibility listeners while a modal is open
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [handleVisibilityChange, handleWindowBlur, handleWindowFocus]);

  const startTracking = useCallback(() => {
    openedAtRef.current = new Date().toISOString();
    focusedMsRef.current = 0;
    focusStartRef.current = isFocused() ? Date.now() : null;
  }, [isFocused]);

  const openLink = useCallback((
    url: string,
    title?: string,
    showOpenInNewTab?: boolean,
    allowCredentials?: boolean,
    trustDomain?: boolean
  ) => {
    startTracking();
    currentUrlRef.current = url;
    currentTitleRef.current = title || '';
    linkModal.openLink(url, title, showOpenInNewTab, allowCredentials, trustDomain);
  }, [linkModal, startTracking]);

  const openAuthenticatedLink = useCallback((url: string, title?: string) => {
    startTracking();
    currentUrlRef.current = url;
    currentTitleRef.current = title || '';
    linkModal.openAuthenticatedLink(url, title);
  }, [linkModal, startTracking]);

  const closeModal = useCallback(() => {
    if (openedAtRef.current && currentUrlRef.current) {
      // Flush any running focus segment
      pauseTimer();

      const closedAt = new Date().toISOString();
      const durationMs = focusedMsRef.current;

      const url = currentUrlRef.current;
      const title = currentTitleRef.current;
      const kind = detectKindFromUrl(url);

      // Fire-and-forget — don't block the close animation
      recordResourceVisit({
        url,
        title,
        kind,
        durationMs,
        openedAt: openedAtRef.current,
        closedAt,
      }).catch(() => {});
    }

    openedAtRef.current = null;
    currentUrlRef.current = '';
    currentTitleRef.current = '';
    focusedMsRef.current = 0;
    focusStartRef.current = null;
    linkModal.closeModal();
  }, [linkModal, recordResourceVisit, pauseTimer]);

  return {
    ...linkModal,
    openLink,
    openAuthenticatedLink,
    closeModal,
  };
};
